import type { Feature } from "@/api/models"

import { isFeatureEnabledInConfig } from "@/hooks/feature-utils"

/** Which provider the voice surface talks to for this tenant. */
export type VoiceEngine = "live" | "realtime"

/** What the person chose on this device; `auto` lets the tenant's flags decide. */
export type VoiceEnginePreference = "auto" | VoiceEngine

export const VOICE_ENGINE_PREFERENCE_KEY = "helloai.voice.engine"

// Set when storage refused the write (private mode, blocked site data), so
// the choice still holds for this page instead of silently doing nothing.
let memoryPreference: VoiceEnginePreference | null = null

export function readVoiceEnginePreference(): VoiceEnginePreference {
  if (memoryPreference !== null) return memoryPreference
  try {
    const stored = window.localStorage.getItem(VOICE_ENGINE_PREFERENCE_KEY)
    return stored === "live" || stored === "realtime" ? stored : "auto"
  } catch {
    return "auto"
  }
}

export function writeVoiceEnginePreference(preference: VoiceEnginePreference): void {
  try {
    if (preference === "auto") window.localStorage.removeItem(VOICE_ENGINE_PREFERENCE_KEY)
    else window.localStorage.setItem(VOICE_ENGINE_PREFERENCE_KEY, preference)
    memoryPreference = null
  } catch {
    memoryPreference = preference
  }
  notifyVoiceEnginePreference()
}

// One store for the preference, so every surface that asks for the engine
// (composer, footer toolbars, pages) changes together when it is switched.
const preferenceListeners = new Set<() => void>()

function notifyVoiceEnginePreference(): void {
  for (const listener of preferenceListeners) listener()
}

export function subscribeVoiceEnginePreference(listener: () => void): () => void {
  preferenceListeners.add(listener)
  return () => {
    preferenceListeners.delete(listener)
  }
}

/** The engines this tenant has enabled, Live first. */
export function availableVoiceEngines(features: Feature[] | undefined): VoiceEngine[] {
  const engines: VoiceEngine[] = []
  if (isFeatureEnabledInConfig(features, "live_voice")) engines.push("live")
  if (isFeatureEnabledInConfig(features, "realtime_audio")) engines.push("realtime")
  return engines
}

/** Apply a device preference to the enabled engines: a chosen engine wins when it is enabled. */
export function chooseVoiceEngine(
  enabled: VoiceEngine[],
  preference: VoiceEnginePreference
): VoiceEngine | null {
  if (preference !== "auto" && enabled.includes(preference)) return preference
  return enabled[0] ?? null
}

/**
 * Pick the voice engine from the resolved feature config (non-hook — safe in
 * route builders and tests). `live_voice` wins when both are enabled so a
 * tenant that has a `gpt-live-1` deployment moves over without a second flag;
 * `null` means no voice surface at all, so the entry points hide the button.
 */
export function resolveVoiceEngine(
  features: Feature[] | undefined,
  preference: VoiceEnginePreference = "auto"
): VoiceEngine | null {
  return chooseVoiceEngine(availableVoiceEngines(features), preference)
}
